const BASE_URL = 'https://cook-ai-backend-production.up.railway.app/v1';

const adminHeaders = (secret: string): Record<string, string> => ({
  'x-admin-secret': secret,
});

const adminJsonHeaders = (secret: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  'x-admin-secret': secret,
});

// ── PUBLIC ───────────────────────────────────────────────────────────────────

export async function getLanguages() {
  const res = await fetch(`${BASE_URL}/languages`);
  if (!res.ok) throw new Error('Failed to fetch languages');
  return res.json();
}

export async function getBlogs(lang: string, page = 1, limit = 10, tag?: string) {
  const params = new URLSearchParams({ lang, page: String(page), limit: String(limit) });
  if (tag) params.set('tag', tag);
  const res = await fetch(`${BASE_URL}/blogs?${params}`);
  if (!res.ok) throw new Error('Failed to fetch blogs');
  return res.json();
}

export async function getBlog(slug: string, lang: string) {
  const res = await fetch(`${BASE_URL}/blogs/${slug}?lang=${lang}`, { cache: 'no-store' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch blog');
  return res.json();
}

export async function getBlogTags(lang: string) {
  const res = await fetch(`${BASE_URL}/blogs/tags?lang=${lang}`);
  if (!res.ok) throw new Error('Failed to fetch tags');
  return res.json();
}

export async function sendScrollBeacon(slug: string, depth: number, sessionId: string) {
  try {
    await fetch(`${BASE_URL}/blogs/${slug}/scroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ depth, session_id: sessionId }),
      keepalive: true,
    });
  } catch {
    // fire-and-forget — ignore errors silently
  }
}

// ── ADMIN ────────────────────────────────────────────────────────────────────

export async function adminGetBlogs(
  secret: string,
  params?: { lang?: string; status?: string; page?: number; limit?: number }
) {
  const qs = new URLSearchParams();
  if (params?.lang) qs.set('lang', params.lang);
  if (params?.status) qs.set('status', params.status);
  qs.set('page', String(params?.page ?? 1));
  qs.set('limit', String(params?.limit ?? 20));
  const res = await fetch(`${BASE_URL}/admin/blogs?${qs}`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Unauthorized or request failed');
  return res.json();
}

export async function adminGetBlog(secret: string, id: string) {
  const res = await fetch(`${BASE_URL}/admin/blogs/${id}`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Failed to fetch blog');
  return res.json();
}

export async function adminCreateBlog(secret: string, data: object) {
  const res = await fetch(`${BASE_URL}/admin/blogs`, {
    method: 'POST',
    headers: adminJsonHeaders(secret),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create blog');
  return res.json();
}

export async function adminUpdateBlog(secret: string, id: string, data: object) {
  const res = await fetch(`${BASE_URL}/admin/blogs/${id}`, {
    method: 'PUT',
    headers: adminJsonHeaders(secret),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update blog');
  return res.json();
}

export async function adminUpdateStatus(secret: string, id: string, status: string) {
  const res = await fetch(`${BASE_URL}/admin/blogs/${id}/status`, {
    method: 'PATCH',
    headers: adminJsonHeaders(secret),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update status');
  return res.json();
}

export async function adminDeleteBlog(secret: string, id: string) {
  const res = await fetch(`${BASE_URL}/admin/blogs/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
  if (!res.ok) throw new Error('Failed to delete blog');
  return res.json();
}

export async function adminGetCoverage(secret: string) {
  const res = await fetch(`${BASE_URL}/admin/blogs/coverage`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Failed to fetch coverage');
  return res.json();
}

export async function adminGetLanguages(secret: string) {
  const res = await fetch(`${BASE_URL}/languages`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Failed to fetch languages');
  return res.json();
}

export async function adminCreateLanguage(secret: string, data: object) {
  const res = await fetch(`${BASE_URL}/admin/languages`, {
    method: 'POST',
    headers: adminJsonHeaders(secret),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create language');
  return res.json();
}

export async function adminUpdateLanguage(secret: string, id: string, data: object) {
  const res = await fetch(`${BASE_URL}/admin/languages/${id}`, {
    method: 'PUT',
    headers: adminJsonHeaders(secret),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update language');
  return res.json();
}

export async function adminDeleteLanguage(secret: string, id: string) {
  const res = await fetch(`${BASE_URL}/admin/languages/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
  if (!res.ok) throw new Error('Failed to delete language');
  return res.json();
}

export async function adminGetOverviewStats(secret: string) {
  const res = await fetch(`${BASE_URL}/admin/stats/overview`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Failed to fetch stats');
  return res.json();
}

export async function adminGetBlogStats(secret: string, id: string) {
  const res = await fetch(`${BASE_URL}/admin/stats/blogs/${id}`, { headers: adminHeaders(secret) });
  if (!res.ok) throw new Error('Failed to fetch blog stats');
  return res.json();
}
